<script>
    import {Player} from '../player.model';
    import {
        beyondTopPlayers,
        countUpPlayers,
        dealADartPlayers,
        highLowPlayers,
        killerPlayers,
        names,
        players,
        roundTheBoardPlayers,
        shanghaiPlayers,
        suddenDeathPlayers,
        tacticsPlayers
    } from '../store';
    import {createEventDispatcher, onMount} from 'svelte';
    import {TacticsPlayer} from "../tactics.model";
    import {CountUpPlayer} from "../countup.model";
    import {BeyondTopPlayer} from "../beyondtop.model";
    import {KillerPlayer} from "../killer.model";
    import {RoundTheBoardPlayer} from "../roundtheboard.model";
    import {HighLowPlayer} from "../highlow.model";

    const dispatch = createEventDispatcher();

    const games = [
        { id: 'half-it', title: 'Half it', minPlayers: 1 },
        { id: 'round-the-board', title: 'Round the board', minPlayers: 1 },
        { id: 'beyond-top', title: 'Beyond Top', minPlayers: 2 },
        { id: 'high-low', title: 'High Low', minPlayers: 2 },
        { id: 'deal-a-dart', title: 'Deal a Dart', minPlayers: 1 },
        { id: 'sudden-death', title: 'Sudden Death', minPlayers: 2 },
        { id: 'tactics', title: 'Tactics', minPlayers: 1 },
        { id: 'shanghai', title: 'Shanghai', minPlayers: 1 },
        { id: 'killer', title: 'Killer (Beta)', minPlayers: 2 },
        { id: 'count-up', title: 'Count Up', minPlayers: 1 }
    ];

    const gameRules = {
        'half-it': {
            title: 'Half it',
            text: `Players start with <strong>40</strong> points, and they must hit the designated number for each round to win additional points. If they miss all three throws, their points are halved.
<br/><br/>
<strong>Feeling lucky</strong> - Spinning wheel with crazy options!
<br/>
<strong>20 - 15</strong> - Rounds are reversed from 20 to 15
<br/>
<strong>Quick game</strong> - Only four rounds and starting with a scoring round`
        },
        'round-the-board': {
            title: 'Round the board',
            text: `Be the first player to hit all the numbers on the dart board from 20 to 1 and 3 bulls. Any segment of the number - single, double or triple - counts as a hit. The numbers must be hit in order, and players change after three throws. A player cannot pass or skip certain numbers, they must hit it to advance to the next number on the board.
<br/><br/>
<strong>Triple</strong> - skip two segments
<br/>
<strong>Double</strong> - skip one segment
<br/>
<strong>Quick game</strong> - Rounds 20 to 10`
        },
        'beyond-top': {
            title: 'Beyond Top',
            text: `A player starts with 6 lives and 10 rounds. The number, 30 is appeared at the center the beginning. You have to get your points over 30. Then you can pass a round successfully and prevent from a loss of your life.
<br/><br/>
After the first round of Player 1, all the players have to get beyond the previous player's points. Otherwise, they fail to pass a round and lose one life. When all lives are gone, the game is over!`
        },
        'high-low': {
            title: 'High Low',
            text: `Score <strong>higher than 45</strong> or <strong>lower than 26</strong> with three scoring darts. A successful high or low score becomes the new target. Miss either target, or fail to score with all three darts, and lose a life. The last player with lives remaining wins.`
        },
        'deal-a-dart': {
            title: 'Deal a Dart',
            text: `The first player to hit the single, double and triple wins`
        },
        'sudden-death': {
            title: 'Sudden Death',
            text: `Sudden Death is an elimination game. Per round, the (remaining) players throw three arrows at the board. The player who hits the lowest score in the round is out of the game.`
        },
        'tactics': {
            title: 'Tactics',
            text: `Hit the numbers 15 to 20 and Bull three times. When you hit a number more than 3 times, and your opponent has not yet hit this number three times, you can score points.`
        },
        'shanghai': {
            title: 'Shanghai',
            text: `You win this dart game by either scoring the most points after all rounds, or scoring a “Shanghai” in any single round.<br/><br/>
<strong>Shanghai</strong> - when a player hits a single, double and triple of the same number in a single turn`
        },
        'killer': {
            title: 'Killer',
            text: `Each player gets a random number assigned. Once you hit your number 6 times you become a killer. A Killer can take lives from all players, including other Killers. The last person with lives left wins the game.
<br/><br/>
<strong>Single</strong> - Counts as one point
<br/>
<strong>Double</strong> - Counts as two points
<br/>
<strong>Triple</strong> - Counts as three points`
        },
        'count-up': {
            title: 'Count Up',
            text: `Score as many points as possible! The player with the most points wins.`
        }
    };

    let selectedGameKey = 'half-it';
    let hoveredGameKey = null;

    $: activeRulesGameKey = hoveredGameKey || selectedGameKey;
    $: activeRules = gameRules[activeRulesGameKey] || gameRules['half-it'];
    $: selectedGame = games.find(g => g.id === selectedGameKey) || games[0];

    let name = '';
    let nameInputElement;

    // Options state
    let funHalfIt = false;
    let halfItRoundModeReversed = false;
    let halfItQuickGame = false;
    let luckyPercentage = 50;

    let roundTheBoardQuickGame = false;
    let roundTheBoardNrOfBulls = 3;

    let beyondTopQuickGame = false;
    let highLowLives = 3;
    let countUpRounds = 5;

    let savedPlayers = [];
    const STORAGE_KEY = 'dart_saved_players';

    onMount(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    savedPlayers = parsed.filter(name => typeof name === 'string' && name.trim().length > 0);
                }
            }
        } catch (e) {
            console.error('Failed to load saved players from localStorage', e);
        }
    });

    function persistSavedPlayer(playerName) {
        if (!playerName) return;
        const trimmed = playerName.trim();
        if (!trimmed) return;
        const exists = savedPlayers.some(p => p.toLowerCase() === trimmed.toLowerCase());
        if (!exists) {
            savedPlayers = [...savedPlayers, trimmed];
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(savedPlayers));
            } catch (e) {
                console.error('Failed to save player to localStorage', e);
            }
        }
    }

    function addPlayer(playerName) {
        const cleanName = (playerName || name || '').trim();
        if (cleanName) {
            const alreadyInGame = $names.some(n => n.toLowerCase() === cleanName.toLowerCase());
            if (!alreadyInGame) {
                names.update(n => [...n, cleanName]);
                persistSavedPlayer(cleanName);
            }
            name = '';
        }
        if (nameInputElement) {
            nameInputElement.focus();
        }
    }

    function removePlayer(playerName) {
        names.update(n => n.filter(p => p !== playerName));
    }

    function onKeyPress(e) {
        if (e.charCode === 13 || e.key === 'Enter') {
            e.preventDefault();
            addPlayer();
        }
    }

    function handleQuickGameToggle() {
        if (halfItQuickGame) {
            halfItRoundModeReversed = false;
        }
    }

    function selectGame(gameId) {
        selectedGameKey = gameId;
    }

    $: isGameDisabled = (game) => {
        return game.minPlayers > 1 && $names.length === 1;
    };

    $: isStartDisabled = () => {
        if ($names.length === 0) return true;
        if (selectedGame.minPlayers > 1 && $names.length < 2) return true;
        return false;
    };

    function startHalfIt() {
        players.set([]);
        names.subscribe((playerNames) => {
            playerNames.forEach(n => {
                players.update(p => [...p, new Player(n, [], halfItQuickGame)]);
            });
        })();

        let luckyLevelValue = luckyPercentage / 10;
        dispatch('startHalfIt', {
            roundModeReversed: halfItRoundModeReversed,
            funHalfIt: funHalfIt,
            luckyLevel: luckyLevelValue,
            halfItQuickGame: halfItQuickGame
        });
    }

    function startRoundTheBoard() {
        roundTheBoardPlayers.set([]);
        names.subscribe((playerNames) => {
            playerNames.forEach(n => {
                roundTheBoardPlayers.update(p => [...p, new RoundTheBoardPlayer(n, [])]);
            });
        })();

        dispatch('startRoundTheBoard', {
            roundTheBoardQuickGame: roundTheBoardQuickGame,
            roundTheBoardNrOfBulls: roundTheBoardNrOfBulls
        });
    }

    function startTactics() {
        tacticsPlayers.set([]);
        names.subscribe((playerNames) => {
            playerNames.forEach(n => {
                tacticsPlayers.update(p => [...p, new TacticsPlayer(n)]);
            });
        })();

        dispatch('startTactics');
    }

    function startKiller() {
        killerPlayers.set([]);
        names.subscribe((playerNames) => {
            playerNames.forEach(n => {
                killerPlayers.update(p => [...p, new KillerPlayer(n)]);
            });
        })();

        dispatch('startKiller');
    }

    function startBeyondTop() {
        beyondTopPlayers.set([]);
        names.subscribe((playerNames) => {
            playerNames.forEach(n => {
                beyondTopPlayers.update(p => [...p, new BeyondTopPlayer(n, 6, 10)]);
            });
        })();

        dispatch('startBeyondTop', {beyondTopQuickGame: beyondTopQuickGame});
    }

    function startHighLow() {
        highLowPlayers.set([]);
        names.subscribe(playerNames => {
            playerNames.forEach(n => {
                highLowPlayers.update(p => [...p, new HighLowPlayer(n, highLowLives)]);
            });
        })();

        dispatch('startHighLow', {lives: highLowLives});
    }

    function startShanghai() {
        shanghaiPlayers.set([]);
        dispatch('startShanghai');
    }

    function startCountUp() {
        countUpPlayers.set([]);
        names.subscribe((playerNames) => {
            playerNames.forEach(n => {
                countUpPlayers.update(p => [...p, new CountUpPlayer(n, countUpRounds)]);
            });
        })();

        dispatch('startCountUp', {nrOfRounds: countUpRounds});
    }

    function startDealADart() {
        dealADartPlayers.set([]);
        dispatch('startDealADart');
    }

    function startSuddenDeath() {
        suddenDeathPlayers.set([]);
        dispatch('startSuddenDeath');
    }

    function handleStartGame() {
        if (isStartDisabled()) return;

        $names.forEach(n => persistSavedPlayer(n));

        switch (selectedGameKey) {
            case 'half-it':
                startHalfIt();
                break;
            case 'round-the-board':
                startRoundTheBoard();
                break;
            case 'beyond-top':
                startBeyondTop();
                break;
            case 'high-low':
                startHighLow();
                break;
            case 'deal-a-dart':
                startDealADart();
                break;
            case 'sudden-death':
                startSuddenDeath();
                break;
            case 'tactics':
                startTactics();
                break;
            case 'shanghai':
                startShanghai();
                break;
            case 'killer':
                startKiller();
                break;
            case 'count-up':
                startCountUp();
                break;
        }
    }
</script>

<div class="start-screen-wrapper w-full">
    <!-- Header bar -->
    <header class="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs h-16 sm:h-20 flex items-center px-6 sm:px-8">
        <img src="images/yuma.png" alt="Yuma Logo" class="h-10 sm:h-14 max-h-14 w-auto object-contain" />
    </header>

    <!-- Main 2-Column Workspace -->
    <main class="flex-1 w-full max-w-7xl mx-auto pt-14 sm:pt-16 pb-28 px-4 sm:px-6">
        <!-- Brand Banner on Top -->
<!--        <div class="text-center py-6 select-none pointer-events-none opacity-95">-->
<!--            <img src="images/yuma.png" alt="Yuma Logo" class="h-24 sm:h-32 md:h-36 w-auto mx-auto object-contain" />-->
<!--        </div>-->

        <div class="grid grid-cols-1 py-6  lg:grid-cols-12 gap-6 items-start mt-2">
            <!-- LEFT COLUMN: Add Players + Rules Panel (col-span-5) -->
            <div class="lg:col-span-5 flex flex-col gap-6">
                <!-- CARD 1: Add players / Spelers toevoegen -->
                <div class="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <!-- Card Header with Teal/Petrol styling -->
                    <div class="px-5 py-4 border-b border-slate-200/90 flex items-center justify-between text-white" style="background-color: #005d46;">
                        <div class="flex items-center gap-2.5">
                            <span class="w-2.5 h-2.5 rounded-full" style="background-color: #21e467;"></span>
                            <h2 class="font-headline font-bold text-base tracking-wide text-white">Add players</h2>
                        </div>
                        <span class="text-xs font-semibold px-2.5 py-0.5 rounded-full font-headline tracking-wide" id="player-badge" style="background-color: #F1ECEA; color: #3b004c;">
                            {$names.length} {$names.length === 1 ? 'player' : 'players'}
                        </span>
                    </div>

                    <div class="p-5 flex flex-col gap-4">
                        <!-- Add Player Row -->
                        <div class="flex items-center gap-2">
                            <input
                                class="flex-1 bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                                id="player-input"
                                placeholder="Player name"
                                type="text"
                                bind:this={nameInputElement}
                                bind:value={name}
                                on:keypress={onKeyPress}
                            />
                            <button
                                class="active:scale-[0.98] font-headline font-bold text-sm px-5 py-2 rounded-lg transition-all shadow-sm flex items-center gap-1.5 shrink-0"
                                id="btn-add-player"
                                style="background-color: #EBA9FF; color: #00281c;"
                                type="button"
                                on:click={() => addPlayer()}
                            >
                                <span class="material-symbols-outlined text-[18px]">person_add</span>
                                <span>Add player</span>
                            </button>
                        </div>

                        <!-- Quick Pick Previous Players (Snelkiezer) -->
                        {#if savedPlayers.length > 0}
                            <div class="flex flex-wrap items-center gap-1.5 pt-0.5">
                                <span class="text-xs text-slate-500 font-medium mr-1">Quick add:</span>
                                {#each savedPlayers as savedName}
                                    <button
                                        class={`text-xs px-2.5 py-1 rounded-full border transition-all font-medium inline-flex items-center gap-1 ${
                                            $names.some(n => n.toLowerCase() === savedName.toLowerCase())
                                                ? 'bg-slate-100 text-slate-400 border-slate-200 opacity-60 cursor-not-allowed'
                                                : 'bg-stone-50 hover:bg-emerald-50 text-slate-700 hover:text-emerald-900 border-slate-200 hover:border-emerald-300 cursor-pointer shadow-2xs'
                                        }`}
                                        disabled={$names.some(n => n.toLowerCase() === savedName.toLowerCase())}
                                        on:click={() => addPlayer(savedName)}
                                        title={$names.some(n => n.toLowerCase() === savedName.toLowerCase()) ? `${savedName} is already added` : `Add ${savedName}`}
                                        type="button"
                                    >
                                        {#if $names.some(n => n.toLowerCase() === savedName.toLowerCase())}
                                            <span class="material-symbols-outlined text-[13px]">check</span>
                                        {:else}
                                            <span>+</span>
                                        {/if}
                                        <span>{savedName}</span>
                                    </button>
                                {/each}
                            </div>
                        {/if}

                        <!-- Player List -->
                        <div class="flex flex-col divide-y divide-slate-100 mt-2" id="player-list">
                            {#if $names.length === 0}
                                <div class="py-3 text-xs text-slate-400 italic text-center">
                                    No players added yet
                                </div>
                            {:else}
                                {#each $names as playerName}
                                    <div class="player-row flex items-center justify-between py-2.5 group">
                                        <div class="flex items-center gap-2.5">
                                            <span class="material-symbols-outlined text-[18px]" style="color: #6434da;">person</span>
                                            <span class="player-name text-sm font-medium text-slate-800">{playerName}</span>
                                        </div>
                                        <button
                                            class="btn-remove text-slate-400 hover:text-red-500 p-1 transition-colors"
                                            title="Remove player"
                                            type="button"
                                            on:click={() => removePlayer(playerName)}
                                        >
                                            <span class="material-symbols-outlined text-[16px]">close</span>
                                        </button>
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>
                </div>

                <!-- CARD 2: Rules / Regels -->
                <div class="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow sticky top-24">
                    <div class="px-5 py-4 border-b border-slate-200/90 flex items-center justify-between text-white" style="background-color: #005d46;">
                        <div class="flex items-center gap-2">
                            <h2 class="font-headline font-bold text-base tracking-wide text-white">Rules</h2>
                        </div>
                        <span class="text-xs font-mono font-bold px-2.5 py-0.5 rounded-full" id="rules-game-tag" style="background-color: #F1ECEA; color: #3b004c;">
                            {activeRules.title}
                        </span>
                    </div>
                    <div class="p-5">
                        <div class="text-sm text-slate-600 leading-relaxed min-h-[72px]" id="rules-content">
                            {@html activeRules.text}
                        </div>
                        <div class="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-1.5">
                            <span class="material-symbols-outlined text-[15px] text-[#2b8a9e]">touch_app</span>
                            <span>Hover over or click a game type to view rules.</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- RIGHT COLUMN: Choose game & Configure options (col-span-7) -->
            <div class="lg:col-span-7 flex flex-col gap-6">
                <!-- STEP 1: Kies speltype -->
                <div class="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-sm" id="game-selection-card">
                    <div class="px-5 py-4 border-b border-slate-200/90 flex items-center justify-between text-white" style="background-color: #005d46;">
                        <div class="flex items-center gap-2.5">
                            <span class="w-5 h-5 rounded-full text-slate-950 font-headline text-xs font-bold flex items-center justify-center" style="background-color: #21e467;">1</span>
                            <h2 class="font-headline font-bold text-base tracking-wide text-white">Choose game</h2>
                        </div>
                    </div>
                    <div class="p-5">
                        <!-- Game Types Grid -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" id="games-grid">
                            {#each games as game}
                                <button
                                    class={`game-card-btn p-3.5 rounded-lg text-left flex items-center justify-between transition-all group active:scale-[0.99] ${
                                        selectedGameKey === game.id
                                            ? 'is-selected shadow-sm'
                                            : 'hover:border-emerald-300 hover:bg-emerald-50/40 text-slate-800'
                                    } ${isGameDisabled(game) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    type="button"
                                    disabled={isGameDisabled(game)}
                                    on:click={() => selectGame(game.id)}
                                    on:mouseenter={() => hoveredGameKey = game.id}
                                    on:mouseleave={() => hoveredGameKey = null}
                                >
                                    {#if selectedGameKey === game.id}
                                        <div class="flex items-center gap-2">
                                            <span class="w-2.5 h-2.5 rounded-full" style="background-color: #005d46;"></span>
                                            <span class="font-headline font-bold text-sm tracking-wide text-[#005d46]">{game.title}</span>
                                        </div>
                                        <span class="material-symbols-outlined text-[20px]" style="color: #005d46;">check_circle</span>
                                    {:else}
                                        <span class="font-headline font-semibold text-sm tracking-wide group-hover:text-slate-900">{game.title}</span>
                                        <span class="material-symbols-outlined text-slate-400 group-hover:text-[#005d46] text-[18px] transition-colors">arrow_forward</span>
                                    {/if}
                                </button>
                            {/each}
                        </div>
                    </div>
                </div>

                <!-- STEP 2: Dedicated Game Options Configuration Panel -->
                <div class="bg-white border-2 rounded-xl overflow-hidden shadow-md border-slate-200" id="game-options-panel">
                    <div class="px-5 py-4 border-b border-slate-200/90 flex items-center justify-between text-white" style="background-color: #005d46;">
                        <div class="flex items-center gap-2.5">
                            <span class="w-5 h-5 rounded-full text-slate-950 font-headline text-xs font-bold flex items-center justify-center" style="background-color: #21e467;">2</span>
                            <div>
                                <h2 class="font-headline font-bold text-base flex items-center gap-2 text-white">
                                    <span>Game options:</span>
                                    <span class="font-bold" style="color: #21e467;">{selectedGame.title}</span>
                                </h2>
                            </div>
                        </div>
                    </div>

                    <div class="p-6 flex flex-col gap-4 bg-slate-50/50">
                        <div id="options-container">
                            <!-- Half it Options -->
                            {#if selectedGameKey === 'half-it'}
                                <div class="game-options-set flex flex-col gap-4">
                                    <div class="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
                                        <span class="material-symbols-outlined text-[16px]" style="color: #6434da;">tune</span>
                                        <span>Adjust game rules and variants for this match:</span>
                                    </div>
                                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <!-- Feeling lucky -->
                                        <label class="flex items-center justify-between p-3.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors shadow-xs">
                                            <div class="flex flex-col">
                                                <span class="text-sm font-semibold text-slate-800">Feeling lucky</span>
                                                <span class="text-[11px] text-slate-500">Random targets</span>
                                            </div>
                                            <input type="checkbox" bind:checked={funHalfIt} class="sr-only peer custom-toggle" />
                                            <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#eba9ff] relative shrink-0"></div>
                                        </label>

                                        <!-- 20 - 15 modus -->
                                        <label class={`flex items-center justify-between p-3.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 transition-colors shadow-xs ${halfItQuickGame ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                            <div class="flex flex-col">
                                                <span class="text-sm font-semibold text-slate-800">20 - 15 mode</span>
                                                <span class="text-[11px] text-slate-500">Standard order</span>
                                            </div>
                                            <input type="checkbox" bind:checked={halfItRoundModeReversed} disabled={halfItQuickGame} class="sr-only peer custom-toggle" />
                                            <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#eba9ff] relative shrink-0"></div>
                                        </label>

                                        <!-- Quick game -->
                                        <label class="flex items-center justify-between p-3.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors shadow-xs">
                                            <div class="flex flex-col">
                                                <span class="text-sm font-semibold text-slate-800">Quick game</span>
                                                <span class="text-[11px] text-slate-500">Shorter rounds</span>
                                            </div>
                                            <input type="checkbox" bind:checked={halfItQuickGame} on:change={handleQuickGameToggle} class="sr-only peer custom-toggle" />
                                            <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#eba9ff] relative shrink-0"></div>
                                        </label>
                                    </div>

                                    {#if funHalfIt}
                                        <div class="p-4 rounded-lg bg-white border border-slate-200 flex flex-col gap-3 shadow-xs mt-2">
                                            <div class="flex justify-between items-center">
                                                <span class="text-sm font-semibold text-slate-800">Lucky percentage!</span>
                                                <span class="text-primary font-bold font-headline text-base bg-primary/10 px-2.5 py-0.5 rounded border border-primary/20">
                                                    {luckyPercentage}%
                                                </span>
                                            </div>
                                            <input type="range" min="10" max="100" step="10" bind:value={luckyPercentage} class="w-full custom-range" />
                                            <div class="flex justify-between text-[11px] text-slate-400 px-0.5 font-mono">
                                                <span>10%</span><span>30%</span><span>50%</span><span>70%</span><span>100%</span>
                                            </div>
                                        </div>
                                    {/if}
                                </div>

                            <!-- Round the board Options -->
                            {:else if selectedGameKey === 'round-the-board'}
                                <div class="game-options-set flex flex-col gap-4">
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                                        <label class="flex items-center justify-between p-3.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors shadow-xs">
                                            <div class="flex flex-col">
                                                <span class="text-sm font-semibold text-slate-800">Quick game (20-10)</span>
                                                <span class="text-[11px] text-slate-500">Half board round</span>
                                            </div>
                                            <input type="checkbox" bind:checked={roundTheBoardQuickGame} class="sr-only peer custom-toggle" />
                                            <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#eba9ff] relative shrink-0"></div>
                                        </label>

                                        <div class="p-3.5 rounded-lg bg-white border border-slate-200 flex flex-col gap-2 shadow-xs">
                                            <div class="flex justify-between items-center text-xs text-slate-600 font-medium">
                                                <span>Target Bulls:</span>
                                                <span class="text-primary font-bold">{roundTheBoardNrOfBulls} {roundTheBoardNrOfBulls === 1 ? 'Bull' : 'Bulls'}</span>
                                            </div>
                                            <input type="range" min="1" max="3" step="1" bind:value={roundTheBoardNrOfBulls} class="w-full custom-range" />
                                            <div class="flex justify-between text-[11px] text-slate-400 px-0.5 font-medium">
                                                <span class={roundTheBoardNrOfBulls === 1 ? 'text-primary font-bold' : ''}>1 Bull</span>
                                                <span class={roundTheBoardNrOfBulls === 2 ? 'text-primary font-bold' : ''}>2 Bulls</span>
                                                <span class={roundTheBoardNrOfBulls === 3 ? 'text-primary font-bold' : ''}>3 Bulls</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            <!-- High Low Options -->
                            {:else if selectedGameKey === 'high-low'}
                                <div class="game-options-set flex flex-col gap-4">
                                    <div class="p-4 rounded-lg bg-white border border-slate-200 flex flex-col gap-3 shadow-xs">
                                        <div class="flex justify-between items-center">
                                            <span class="text-sm font-semibold text-slate-800">Number of lives</span>
                                            <span class="text-primary font-bold font-headline text-base bg-primary/10 px-2.5 py-0.5 rounded border border-primary/20">
                                                {highLowLives} {highLowLives === 1 ? 'life' : 'lives'}
                                            </span>
                                        </div>
                                        <input type="range" min="1" max="10" step="1" bind:value={highLowLives} class="w-full custom-range" />
                                        <div class="flex justify-between text-[11px] text-slate-400 px-0.5 font-mono">
                                            {#each [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as num}
                                                <span class={highLowLives === num ? 'text-primary font-bold' : ''}>{num}</span>
                                            {/each}
                                        </div>
                                    </div>
                                </div>

                            <!-- Count Up Options -->
                            {:else if selectedGameKey === 'count-up'}
                                <div class="game-options-set flex flex-col gap-4">
                                    <div class="p-4 rounded-lg bg-white border border-slate-200 flex flex-col gap-3 shadow-xs">
                                        <div class="flex justify-between items-center">
                                            <span class="text-sm font-semibold text-slate-800">Number of rounds</span>
                                            <span class="text-primary font-bold font-headline text-base bg-primary/10 px-2.5 py-0.5 rounded border border-primary/20">
                                                {countUpRounds} {countUpRounds === 1 ? 'round' : 'rounds'}
                                            </span>
                                        </div>
                                        <input type="range" min="5" max="10" step="1" bind:value={countUpRounds} class="w-full custom-range" />
                                        <div class="flex justify-between text-[11px] text-slate-400 px-0.5 font-mono">
                                            {#each [5, 6, 7, 8, 9, 10] as num}
                                                <span class={countUpRounds === num ? 'text-primary font-bold' : ''}>{num}</span>
                                            {/each}
                                        </div>
                                    </div>
                                </div>

                            <!-- Beyond Top Options -->
                            {:else if selectedGameKey === 'beyond-top'}
                                <div class="game-options-set flex flex-col gap-4">
                                    <div class="p-3.5 rounded-lg bg-white border border-slate-200 flex items-center justify-between shadow-xs">
                                        <div class="flex flex-col">
                                            <span class="text-sm font-semibold text-slate-800">Quick game (5 rounds / 3 lives)</span>
                                            <span class="text-[11px] text-slate-500">Accelerated match mode</span>
                                        </div>
                                        <label class="inline-flex items-center gap-2 cursor-pointer select-none">
                                            <input type="checkbox" bind:checked={beyondTopQuickGame} class="sr-only peer custom-toggle" />
                                            <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#eba9ff] relative shrink-0"></div>
                                        </label>
                                    </div>
                                </div>

                            <!-- Generic Standard Mode Option for other games -->
                            {:else}
                                <div class="game-options-set flex flex-col gap-3">
                                    <div class="p-3.5 rounded-lg bg-white border border-slate-200 flex items-center justify-between shadow-xs">
                                        <div class="flex flex-col">
                                            <span class="text-sm font-semibold text-slate-800">Official match rules</span>
                                            <span class="text-[11px] text-slate-500">Standard settings active for this match mode</span>
                                        </div>
                                        <span class="material-symbols-outlined text-primary text-[20px]">verified</span>
                                    </div>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>

    <!-- Sticky Bottom Launch Dock -->
    <div class="fixed bottom-0 left-0 w-full z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 py-3.5 shadow-lg">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
            <div class="flex items-center gap-2.5 text-sm text-slate-600">
                <span class="w-2.5 h-2.5 rounded-full animate-pulse" style="background-color: #21e467;"></span>
                <span>Ready to start: <strong class="text-slate-900 font-headline font-bold ml-1">{selectedGame.title}</strong></span>
                {#if $names.length === 0}
                    <span class="text-xs text-amber-600 font-medium ml-2">(Add players to start match)</span>
                {:else if selectedGame.minPlayers > 1 && $names.length < 2}
                    <span class="text-xs text-amber-600 font-medium ml-2">(Minimum 2 players required)</span>
                {/if}
            </div>
            <button
                class="active:scale-[0.98] font-headline font-bold text-sm sm:text-base px-7 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                id="btn-start"
                style="background-color: #EBA9FF; color: #00281c;"
                type="button"
                disabled={isStartDisabled()}
                on:click={handleStartGame}
            >
                <span>Start Match</span>
                <span class="material-symbols-outlined text-[20px]" style="color: #00281c;">play_arrow</span>
            </button>
        </div>
    </div>
</div>

<style>
    .start-screen-wrapper {
        min-height: 100vh;
        background-color: #f1ecea;
        font-family: 'Inter', sans-serif;
    }

    #player-input {
        border: 1px solid #e2e8f0;
    }

    #player-input:focus {
        border-color: #005262;
        outline: none;
        box-shadow: 0 0 0 1px #005262;
    }

    .game-card-btn {
        border: 2px solid #e2e8f0;
        background-color: #ffffff;
    }

    .game-card-btn:hover:not(:disabled):not(.is-selected) {
        border-color: #a7f3d0;
        background-color: #f0fdf4;
    }

    .game-card-btn.is-selected {
        border: 2px solid #005d46 !important;
        background-color: #f0fdf4;
    }

    input[type=range].custom-range {
        -webkit-appearance: none;
        appearance: none;
        background: #e2e8f0;
        height: 6px;
        border-radius: 9999px;
        outline: none;
    }

    input[type=range].custom-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        height: 18px;
        width: 18px;
        border-radius: 50%;
        background: #005262;
        cursor: pointer;
        box-shadow: 0 1px 4px rgba(0, 82, 98, 0.3);
    }
</style>
